import {
  User,
  Department,
  Plant,
  CostCenter,
  PurchaseRequest,
  NoteForApproval,
  LetterForAward,
  Rfq,
  Quotation,
  PurchaseOrder,
  GoodsReceiptNote,
  Invoice,
  Payment,
  Vendor,
  Notification,
  AuditLog,
  IntegrationLog,
  Material,
} from '@/types/domain';
import { ROLE_PERMISSIONS_MAP, DEFAULT_ROLE_SCOPES } from '@/config/permissions';
import {
  randomInt,
  randomDateISO,
  generateEnterpriseVendorName,
} from './seeder';

export type CompanyCode = { code: string; name: string; currency: string; country: string; isActive: boolean };
export type PurchasingOrganization = { code: string; name: string; companyCode: string; isActive: boolean };
export type PurchasingGroup = { code: string; name: string; email: string; isActive: boolean };
export type GLAccount = { code: string; name: string; category: string; isActive: boolean };

// ============================================================================
// 1. MASTER DATA (Company Codes, Plants, Departments, Cost Centers, Users, Materials, Vendors)
// ============================================================================

export const MOCK_COMPANY_CODES: CompanyCode[] = [
  { code: '1000', name: 'ProcureAI North America Inc.', currency: 'USD', country: 'US', isActive: true },
  { code: '2000', name: 'ProcureAI Europe GmbH', currency: 'EUR', country: 'DE', isActive: true },
  { code: '3000', name: 'ProcureAI Asia Pacific Pte.', currency: 'SGD', country: 'SG', isActive: true },
  { code: '4000', name: 'ProcureAI India Private Ltd.', currency: 'INR', country: 'IN', isActive: true },
];

export const MOCK_PLANTS: Plant[] = [
  { id: 'p1', code: 'PLANT-1000', name: 'Plant 1000 — Mumbai Central Hub', location: 'Mumbai, MH', companyCode: '1000', isActive: true },
  { id: 'p2', code: 'PLANT-1020', name: 'Plant 1020 — Gurugram Tech Facility', location: 'Gurugram, HR', companyCode: '1000', isActive: true },
  { id: 'p3', code: 'PLANT-1030', name: 'Plant 1030 — Bengaluru R&D Campus', location: 'Bengaluru, KA', companyCode: '1000', isActive: true },
  { id: 'p4', code: 'PLANT-1040', name: 'Plant 1040 — Chennai Heavy Fabrication', location: 'Chennai, TN', companyCode: '1000', isActive: true },
  { id: 'p5', code: 'PLANT-1050', name: 'Plant 1050 — Pune Assembly Plant', location: 'Pune, MH', companyCode: '1000', isActive: true },
  { id: 'p6', code: 'PLANT-2000', name: 'Plant 2000 — Frankfurt Innovation Center', location: 'Frankfurt, DE', companyCode: '2000', isActive: true },
  { id: 'p7', code: 'PLANT-3000', name: 'Plant 3000 — Singapore Logistics Hub', location: 'Singapore', companyCode: '3000', isActive: true },
  { id: 'p8', code: 'PLANT-4000', name: 'Plant 4000 — Hyderabad Sourcing Hub', location: 'Hyderabad, IN', companyCode: '4000', isActive: true },
  { id: 'p9', code: 'PLANT-5000', name: 'Plant 5000 — Chicago Manufacturing Plant', location: 'Chicago, IL', companyCode: '1000', isActive: true },
  { id: 'p10', code: 'PLANT-6000', name: 'Plant 6000 — Munich Assembling Facility', location: 'Munich, DE', companyCode: '2000', isActive: true },
];

export const MOCK_DEPARTMENTS: Department[] = [
  { id: 'd1', code: 'DEP-IT', name: 'Information Technology', costCenterCode: 'CC-IT-101', managerId: 'usr-2', isActive: true },
  { id: 'd2', code: 'DEP-PROC', name: 'Global Sourcing & Procurement', costCenterCode: 'CC-PROC-201', managerId: 'usr-3', isActive: true },
  { id: 'd3', code: 'DEP-FIN', name: 'Enterprise Finance & Treasury', costCenterCode: 'CC-FIN-301', managerId: 'usr-4', isActive: true },
  { id: 'd4', code: 'DEP-ENG', name: 'Research & Product Engineering', costCenterCode: 'CC-ENG-401', managerId: 'usr-6', isActive: true },
  { id: 'd5', code: 'DEP-OPS', name: 'Manufacturing Operations & Supply Chain', costCenterCode: 'CC-OPS-501', managerId: 'usr-7', isActive: true },
  { id: 'd6', code: 'DEP-HR', name: 'Human Resources & Talent', costCenterCode: 'CC-HR-601', managerId: 'usr-8', isActive: true },
  { id: 'd7', code: 'DEP-LEGAL', name: 'Legal, Risk & Compliance', costCenterCode: 'CC-LEGAL-701', managerId: 'usr-9', isActive: true },
  { id: 'd8', code: 'DEP-FAC', name: 'Facilities & Infrastructure', costCenterCode: 'CC-FAC-801', managerId: 'usr-10', isActive: true },
];

export const MOCK_PURCHASING_ORGANIZATIONS: PurchasingOrganization[] = [
  { code: 'POG-1000', name: 'Global Procurement Org', companyCode: '1000', isActive: true },
  { code: 'POG-2000', name: 'EMEA Regional Sourcing Org', companyCode: '2000', isActive: true },
  { code: 'POG-3000', name: 'APAC Direct Procurement', companyCode: '3000', isActive: true },
  { code: 'POG-4000', name: 'India Shared Services Sourcing', companyCode: '4000', isActive: true },
];

export const MOCK_PURCHASING_GROUPS: PurchasingGroup[] = [
  { code: 'PGRP-001', name: 'IT Hardware & Cloud Services', email: 'pur.ithw@procureai.enterprise', isActive: true },
  { code: 'PGRP-002', name: 'Industrial Equipment & MRO', email: 'pur.mro@procureai.enterprise', isActive: true },
  { code: 'PGRP-003', name: 'Raw Chemicals & Materials', email: 'pur.chem@procureai.enterprise', isActive: true },
  { code: 'PGRP-004', name: 'Facility & Office Supplies', email: 'pur.facilities@procureai.enterprise', isActive: true },
  { code: 'PGRP-005', name: 'Professional & Legal Services', email: 'pur.services@procureai.enterprise', isActive: true },
];

export const MOCK_COST_CENTERS: CostCenter[] = [
  { id: 'cc1', code: 'CC-IT-101', name: 'IT Infrastructure & Cloud', departmentCode: 'DEP-IT', budgetLimit: 2500000, budgetUsed: 1185000, currency: 'USD', isActive: true },
  { id: 'cc2', code: 'CC-PROC-201', name: 'Sourcing Operations', departmentCode: 'DEP-PROC', budgetLimit: 1200000, budgetUsed: 492000, currency: 'USD', isActive: true },
  { id: 'cc3', code: 'CC-FIN-301', name: 'Finance Systems & Treasury', departmentCode: 'DEP-FIN', budgetLimit: 1800000, budgetUsed: 840000, currency: 'USD', isActive: true },
  { id: 'cc4', code: 'CC-ENG-401', name: 'Hardware R&D Lab', departmentCode: 'DEP-ENG', budgetLimit: 4500000, budgetUsed: 2150000, currency: 'USD', isActive: true },
  { id: 'cc5', code: 'CC-OPS-501', name: 'Plant Equipment Maintenance', departmentCode: 'DEP-OPS', budgetLimit: 3800000, budgetUsed: 1940000, currency: 'USD', isActive: true },
];

export const MOCK_GL_ACCOUNTS: GLAccount[] = [
  { code: '500100', name: 'Capital Equipment - Computer Hardware', category: 'CAPEX', isActive: true },
  { code: '500200', name: 'Software Subscriptions & SaaS', category: 'OPEX', isActive: true },
  { code: '600100', name: 'Industrial Machinery Spares', category: 'OPEX', isActive: true },
  { code: '600300', name: 'Facility Maintenance & Utilities', category: 'OPEX', isActive: true },
  { code: '700100', name: 'Professional Consulting Fees', category: 'OPEX', isActive: true },
];

// --- 110 User Records Generator ---
const FIRST_NAMES = ['David', 'Sarah', 'Michael', 'Elena', 'James', 'Priya', 'Robert', 'Anita', 'Chen', 'Maria', 'Alex', 'Rachel', 'Thomas', 'Kavita', 'Vikram'];
const LAST_NAMES = ['Sterling', 'Jenkins', 'Vance', 'Rostova', 'Oakley', 'Sharma', 'Vanderbilt', 'Deshmukh', 'Wei', 'Gonzalez', 'Mercer', 'Vance', 'Thorne', 'Patel', 'Singh'];

export const MOCK_USERS: User[] = Array.from({ length: 110 }, (_, i) => {
  const idNum = i + 1;
  const firstName = FIRST_NAMES[i % FIRST_NAMES.length]!;
  const lastName = LAST_NAMES[(i * 3) % LAST_NAMES.length]!;
  const employeeId = `EMP-${String(9000 + idNum).padStart(4, '0')}`;
  const dept = MOCK_DEPARTMENTS[idNum % MOCK_DEPARTMENTS.length]!;
  const plant = MOCK_PLANTS[idNum % MOCK_PLANTS.length]!;

  let fullName = `${firstName} ${lastName}`;
  let role: User['role'] = 'EMPLOYEE';
  let email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}${idNum > 20 ? idNum : ''}@procure.com`;
  let designation = 'Senior Requisitioner';
  let isSystemUser = false;

  // First 10 users match standard enterprise credentials
  if (idNum === 1) {
    fullName = 'Alexander Hamilton';
    role = 'ADMIN';
    email = 'admin@procure.com';
    designation = 'Chief Technology Officer & Admin';
  } else if (idNum === 2) {
    fullName = 'Marcus Vance';
    role = 'MANAGER';
    email = 'manager@procure.com';
    designation = 'General Manager of IT Services';
  } else if (idNum === 3) {
    fullName = 'Elena Rostova';
    role = 'PROCUREMENT';
    email = 'procurement@procure.com';
    designation = 'Senior Strategic Sourcing Officer';
  } else if (idNum === 4) {
    fullName = 'Rachel Chen';
    role = 'FINANCE';
    email = 'finance@procure.com';
    designation = 'Corporate Finance Controller';
  } else if (idNum === 5) {
    fullName = 'Sarah Jenkins';
    role = 'EMPLOYEE';
    email = 'employee@procure.com';
    designation = 'Operations Project Engineer';
  } else if (idNum === 6) {
    fullName = 'David Sterling';
    role = 'PROCUREMENT_MANAGER';
    email = 'procmanager@procure.com';
    designation = 'Director of Sourcing & Logistics';
  } else if (idNum === 7) {
    fullName = 'Arthur Pendelton';
    role = 'NFA_LFA_APPROVER';
    email = 'approver@procure.com';
    designation = 'VP Commercial Governance';
  } else if (idNum === 8) {
    fullName = 'Victoria Thorne';
    role = 'MANAGEMENT';
    email = 'management@procure.com';
    designation = 'Chief Procurement Officer';
  } else if (idNum === 9) {
    fullName = 'SAP Integration Gateway';
    role = 'SAP_INTEGRATION';
    email = 'sap@procure.com';
    designation = 'Enterprise System Connector';
    isSystemUser = true;
  } else if (idNum === 10) {
    fullName = 'Grace Hopper';
    role = 'AUDITOR';
    email = 'auditor@procure.com';
    designation = 'Lead Internal Compliance Auditor';
  } else {
    // Role distribution for remaining users
    if (idNum % 11 === 0) {
      role = 'ADMIN';
      designation = 'Systems Administrator';
    } else if (idNum % 5 === 0) {
      role = 'MANAGER';
      designation = 'Departmental Operations Manager';
    } else if (idNum % 4 === 0) {
      role = 'PROCUREMENT';
      designation = 'Sourcing Category Specialist';
    } else if (idNum % 7 === 0) {
      role = 'FINANCE';
      designation = 'Accounts Payable Specialist';
    } else if (idNum % 13 === 0) {
      role = 'PROCUREMENT_MANAGER';
      designation = 'Strategic Sourcing Lead';
    } else if (idNum % 17 === 0) {
      role = 'AUDITOR';
      designation = 'Vigilance & Audit Officer';
    }
  }

  // Realistic Enterprise Lifecycle distribution
  let status: User['status'] = 'ACTIVE';
  let invitationStatus: User['invitationStatus'] = 'ACCEPTED';
  let lastLoginAt: string | undefined = '2026-09-02T14:30:00Z';

  if (idNum > 10) {
    if (idNum % 9 === 0) {
      status = 'INVITATION_PENDING';
      invitationStatus = 'INVITATION_PENDING';
      lastLoginAt = undefined;
    } else if (idNum % 19 === 0) {
      status = 'INACTIVE';
      lastLoginAt = '2026-08-10T11:15:00Z';
    } else if (idNum % 23 === 0) {
      status = 'BLOCKED';
      lastLoginAt = '2026-08-20T09:45:00Z';
    } else if (idNum % 29 === 0) {
      status = 'SUSPENDED';
      lastLoginAt = '2026-08-28T16:20:00Z';
    } else if (idNum % 31 === 0) {
      status = 'LOCKED';
      lastLoginAt = '2026-09-01T10:05:00Z';
    }
  }

  const phone = `+91 98${String(10000000 + idNum * 87654).slice(0, 8)}`;
  const costCenterCode = dept.costCenterCode || `CC-10${idNum % 5}0`;
  const managerId = idNum === 2 ? 'usr-1' : 'usr-2';
  const managerName = idNum === 2 ? 'Alexander Hamilton' : 'Marcus Vance';

  return {
    id: `usr-${idNum}`,
    employeeId,
    name: fullName,
    email,
    phone,
    designation,
    role,
    roles: [role],
    departmentCode: dept.code,
    departmentName: dept.name,
    plantCode: plant.code,
    businessUnit: 'BU-GLOBAL',
    companyCode: plant.companyCode,
    costCenterCode,
    managerId,
    managerName,
    approvalAuthorityLimit:
      role === 'ADMIN'
        ? 99999999
        : role === 'MANAGEMENT'
        ? 10000000
        : role === 'NFA_LFA_APPROVER'
        ? 5000000
        : role === 'PROCUREMENT_MANAGER'
        ? 1000000
        : role === 'FINANCE'
        ? 500000
        : role === 'PROCUREMENT'
        ? 250000
        : role === 'MANAGER'
        ? 50000
        : 10000,
    status,
    invitationStatus,
    invitationSentAt: status === 'INVITATION_PENDING' ? '2026-08-30T10:00:00Z' : undefined,
    lastLoginAt,
    createdAt: '2026-06-01T08:00:00Z',
    updatedAt: '2026-08-25T14:20:00Z',
    isSystemUser,
    mfaEnabled: idNum % 3 === 0,
    ssoEnabled: true,
    accessScope: DEFAULT_ROLE_SCOPES[role] || DEFAULT_ROLE_SCOPES.EMPLOYEE,
    permissions: ROLE_PERMISSIONS_MAP[role] || ROLE_PERMISSIONS_MAP.EMPLOYEE,
  };
});

// --- 500+ Material Master Records Generator ---
const MATERIAL_CATEGORIES = ['IT Hardware', 'Enterprise Software', 'Industrial Machinery', 'Raw Materials', 'MRO Spares', 'Office Supplies', 'Logistics Services'];

export const MOCK_MATERIALS: Material[] = Array.from({ length: 520 }, (_, i) => {
  const codeNum = 10000 + i + 1;
  const category = MATERIAL_CATEGORIES[i % MATERIAL_CATEGORIES.length]!;
  return {
    id: `mat-${i + 1}`,
    code: `MAT-${codeNum}`,
    name: `${category} Specification Model #${codeNum}`,
    category,
    unitOfMeasure: i % 4 === 0 ? 'EA' : i % 4 === 1 ? 'MONTH' : i % 4 === 2 ? 'SET' : 'LOT',
    standardPrice: 150 + (i % 80) * 45,
    currency: 'USD',
    sapMaterialGroup: category.slice(0, 4).toUpperCase(),
    isActive: true,
  };
});

// --- 100+ Vendor Master Records Generator ---
const VENDOR_CATEGORIES = ['IT Hardware', 'Cloud & SaaS', 'Industrial Spares', 'Chemicals', 'Facilities', 'Logistics', 'Consulting Services'];

export const MOCK_VENDORS: Vendor[] = Array.from({ length: 120 }, (_, i) => {
  const idIndex = i + 1;
  const vName = generateEnterpriseVendorName(idIndex);
  const vendorCode = `VND-${100 + idIndex}`;
  const category = VENDOR_CATEGORIES[idIndex % VENDOR_CATEGORIES.length]!;

  return {
    id: `vend-${idIndex}`,
    vendorCode,
    name: vName,
    legalName: `${vName} Legal Entity Inc.`,
    contactPerson: `Account Manager ${idIndex}`,
    email: `contact@vendor${idIndex}.enterprise`,
    phone: `+1 555-01${10 + (idIndex % 80)}`,
    address: `${100 + idIndex * 12} Industrial Parkway, Suite ${idIndex}, Austin, TX 78701`,
    category,
    rating: Number((3.8 + (idIndex % 13) * 0.09).toFixed(1)),
    performanceScore: 80 + (idIndex % 20),
    status: idIndex % 15 === 0 ? 'PendingOnboarding' : idIndex % 12 === 0 ? 'UnderReview' : 'Active',
    taxIdentificationNumber: `US-TAX-${900000 + idIndex}`,
    bankAccountDetails: `**** **** 458900${idIndex}`,
    sapVendorCode: `VND-SAP-${1000 + idIndex}`,
    onboardedAt: randomDateISO(180, 10),
  };
});

// ============================================================================
// 2. TRANSACTIONAL DATA (500+ Requests, 500+ NFAs/LFAs, 500+ PRs, 500+ RFQs, 1000+ Quotes, 500+ POs, 500+ GRNs, 500+ Invoices, 500+ Payments)
// ============================================================================

const REQUEST_STATUSES: PurchaseRequest['status'][] = [
  'Draft', 'PendingApproval', 'UnderReview', 'Approved', 'Rejected', 'Completed', 'Cancelled'
];

export const MOCK_PURCHASE_REQUESTS: PurchaseRequest[] = Array.from({ length: 510 }, (_, i) => {
  const reqNum = 1001 + i;
  const requestId = `req-${reqNum}`;
  const requester = MOCK_USERS[i % MOCK_USERS.length]!;
  const dept = MOCK_DEPARTMENTS[i % MOCK_DEPARTMENTS.length]!;
  const plant = MOCK_PLANTS[i % MOCK_PLANTS.length]!;
  const costCenter = MOCK_COST_CENTERS[i % MOCK_COST_CENTERS.length]!;
  const material = MOCK_MATERIALS[i % MOCK_MATERIALS.length]!;
  const quantity = randomInt(2, 50);
  const totalAmount = Math.round(material.standardPrice * quantity * 100) / 100;
  const status = REQUEST_STATUSES[i % REQUEST_STATUSES.length]!;

  return {
    id: requestId,
    requestNumber: `REQ-${reqNum}`,
    title: `${material.name} Procurement for ${dept.name}`,
    requesterId: requester.id,
    requesterName: requester.name,
    departmentId: dept.id,
    departmentCode: dept.code,
    departmentName: dept.name,
    plantId: plant.id,
    plantCode: plant.code,
    costCenterId: costCenter.id,
    costCenterCode: costCenter.code,
    category: (material.category === 'IT Hardware'
      ? 'IT Hardware'
      : material.category === 'Enterprise Software' || material.category === 'Office Supplies' || material.category === 'MRO Spares'
      ? 'OperatingExpense'
      : material.category === 'Industrial Machinery'
      ? 'CapitalEquipment'
      : 'RawMaterial') as any,
    justification: `Procurement required to support departmental capacity expansion and project SLA compliance at ${plant.name}.`,
    totalAmount,
    currency: 'INR',
    budgetCheckPassed: true,
    status,
    items: [
      {
        id: `item-${reqNum}-1`,
        requestId,
        itemNumber: 10,
        materialCode: material.code,
        materialName: material.name,
        category: (material.category === 'IT Hardware'
          ? 'IT Hardware'
          : material.category === 'Enterprise Software' || material.category === 'Office Supplies' || material.category === 'MRO Spares'
          ? 'OperatingExpense'
          : material.category === 'Industrial Machinery'
          ? 'CapitalEquipment'
          : 'RawMaterial') as any,
        quantity,
        unitOfMeasure: material.unitOfMeasure,
        estimatedUnitPrice: material.standardPrice,
        totalEstimatedPrice: totalAmount,
        currency: 'INR',
        requiredDate: '2026-10-15',
        specifications: `Standard enterprise spec model ${material.code}`,
        costCenterCode: costCenter.code,
        plantCode: plant.code,
        glAccountCode: '500100',
      },
    ],
    approvalChain: [
      { id: `app-${reqNum}-1`, stepNumber: 1, approverRole: 'Department Manager', approverName: MOCK_USERS[1]!.name, status: status === 'Approved' || status === 'Completed' ? 'Approved' : 'Pending', actionDate: '2026-08-28T10:30:00Z' },
      { id: `app-${reqNum}-2`, stepNumber: 2, approverRole: 'Finance Controller', approverName: MOCK_USERS[3]!.name, status: status === 'Approved' || status === 'Completed' ? 'Approved' : 'Pending' },
    ],
    createdAt: randomDateISO(120, 10),
    updatedAt: randomDateISO(10, 0),
  };
});

// --- 500+ NFAs & LFAs ---
export const MOCK_NFA: NoteForApproval[] = MOCK_PURCHASE_REQUESTS.map((req, i) => {
  const nfaNum = 1001 + i;
  return {
    id: `nfa-${nfaNum}`,
    nfaNumber: `NFA-${nfaNum}`,
    purchaseRequestId: req.id,
    requestNumber: req.requestNumber,
    title: req.title,
    subject: `NFA: ${req.title}`,
    backgroundContext: `NFA generated for request ${req.requestNumber} covering ${req.items[0]?.materialName || 'Hardware'}.`,
    commercialJustification: `Vendor selected via competitive sourcing. Negotiated volume pricing discount applied.`,
    estimatedCost: req.totalAmount,
    approvedCost: Math.round(req.totalAmount * 0.95 * 100) / 100,
    currency: 'USD',
    savingsEstimated: Math.round(req.totalAmount * 0.05 * 100) / 100,
    budgetLineItem: `${req.costCenterCode} (${req.category})`,
    costCenterCode: req.costCenterCode,
    departmentCode: req.departmentCode || 'DEPT-100',
    departmentName: req.departmentName,
    plantCode: req.plantCode,
    plantName: req.plantCode,
    capexOpex: req.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX',
    procurementCategory: req.items[0]?.category || 'General',
    status: req.status === 'Completed' ? 'Approved' : req.status === 'Rejected' ? 'Rejected' : 'UnderApproval',
    version: 1,
    workflowType: 'NFA',
    approvalMode: 'SEQUENTIAL',
    matchedRule: {
      ruleId: 'RULE-STD-01',
      ruleName: 'Standard Threshold Approval',
      ruleVersion: '1.0',
      effectiveFrom: '2026-01-01',
      matchedConditions: [{ condition: 'Threshold Check', passed: true }],
    },
    approvalChain: [
      {
        id: `nfa-stg-${nfaNum}-1`,
        stageNumber: 1,
        stageName: 'Department Manager Review',
        approverRole: 'Department Manager',
        approverUserId: MOCK_USERS[1]!.id,
        approverName: MOCK_USERS[1]!.name,
        approverEmail: MOCK_USERS[1]!.email,
        approvalMode: 'SEQUENTIAL',
        status: req.status === 'Completed' ? 'Approved' : 'Pending',
        isMandatory: true,
        actionDate: '2026-08-28T14:30:00Z',
      },
    ],
    delegations: [],
    clarifications: [],
    sla: { slaHours: 24, targetTimestamp: '2026-09-05T12:00:00Z', remainingMinutes: 120, status: 'OnTrack', reminderSent: false },
    preparedByUserId: req.requesterId,
    preparedByName: req.requesterName,
    preparedByRole: 'Requisitioner',
    createdAt: req.createdAt,
    updatedAt: req.updatedAt,
  };
});

export const MOCK_LFA: LetterForAward[] = MOCK_NFA.filter((_, i) => i % 2 === 0).map((nfa, i) => {
  const lfaNum = 1001 + i;
  const vendor = MOCK_VENDORS[i % MOCK_VENDORS.length]!;
  return {
    id: `lfa-${lfaNum}`,
    lfaNumber: `LFA-${lfaNum}`,
    nfaId: nfa.id,
    nfaNumber: nfa.nfaNumber,
    purchaseRequestId: nfa.purchaseRequestId,
    requestNumber: nfa.requestNumber,
    title: `Letter of Award for ${nfa.title}`,
    rfqId: `rfq-${2001 + i}`,
    rfqNumber: `RFQ-${2001 + i}`,
    quotationId: `quo-${3001 + i}`,
    vendorId: vendor.id,
    vendorName: vendor.name,
    vendorCode: `VND-${100 + i}`,
    awardedValue: nfa.approvedCost || nfa.estimatedCost,
    currency: 'USD',
    effectiveDate: nfa.createdAt,
    expiryDate: '2027-12-31',
    paymentTerms: 'Net 30',
    deliveryTerms: 'DDP',
    status: nfa.status === 'Approved' ? 'Awarded' : 'Draft',
    version: 1,
    issuedByUserId: 'usr-1',
    issuedByName: 'David Sterling',
    createdAt: nfa.createdAt,
    updatedAt: nfa.updatedAt,
  };
});

// --- 500+ Purchase Requisitions (PRs) ---
export const MOCK_PRS = MOCK_PURCHASE_REQUESTS.map((req, i) => {
  const prNum = 450001 + i;
  return {
    id: `pr-${prNum}`,
    prNumber: `PR-${prNum}`,
    requestId: req.id,
    requestNumber: req.requestNumber,
    title: req.title,
    requesterName: req.requesterName,
    departmentName: req.departmentName,
    plantCode: req.plantCode,
    totalValue: req.totalAmount,
    currency: 'USD',
    status: req.status === 'Completed' ? 'SAP_CREATED' : req.status === 'Rejected' ? 'CANCELLED' : 'SUBMITTED',
    sapPrNumber: req.status === 'Completed' ? `100${prNum}` : undefined,
    sapSyncStatus: req.status === 'Completed' ? ('SUCCESS' as const) : ('PENDING' as const),
    lineItemsCount: req.items.length,
    createdAt: req.createdAt,
    updatedAt: req.updatedAt,
  };
});

// --- 500+ Requests for Quotation (RFQs) ---
export const MOCK_RFQS: Rfq[] = Array.from({ length: 510 }, (_, i) => {
  const req = MOCK_PURCHASE_REQUESTS[i % MOCK_PURCHASE_REQUESTS.length]!;
  const rfqNum = 2001 + i;
  const vendors = [MOCK_VENDORS[i % MOCK_VENDORS.length]!, MOCK_VENDORS[(i + 1) % MOCK_VENDORS.length]!];

  return {
    id: `rfq-${rfqNum}`,
    rfqNumber: `RFQ-${rfqNum}`,
    title: `RFQ: ${req.title}`,
    purchaseRequestId: req.id,
    requestNumber: req.requestNumber,
    category: req.category,
    items: req.items.map((item, idx) => ({
      id: `rfq-item-${rfqNum}-${idx + 1}`,
      materialCode: item.materialCode,
      materialName: item.materialName,
      quantity: item.quantity,
      unitOfMeasure: item.unitOfMeasure,
      targetUnitPrice: item.estimatedUnitPrice,
    })),
    invitedVendorIds: vendors.map((v) => v.id),
    invitedVendorNames: vendors.map((v) => v.name),
    submissionDeadline: '2026-09-25T23:59:59Z',
    status: req.status === 'Approved' ? 'Published' : 'Draft',
    termsAndConditions: 'Standard Enterprise Terms and Conditions',
    createdAt: req.createdAt,
  };
});

// --- 1,000+ Quotations ---
export const MOCK_QUOTATIONS: Quotation[] = MOCK_RFQS.flatMap((rfq, i) => {
  const numQuotes = (i % 3) + 1; // 1 to 3 quotations per RFQ
  return Array.from({ length: numQuotes }, (_, qIdx) => {
    const quoNum = 3001 + i * 2 + qIdx;
    const vendor = MOCK_VENDORS[(i + qIdx) % MOCK_VENDORS.length]!;
    const unitPrice = rfq.items[0] ? (rfq.items[0].targetUnitPrice || 500) * (0.92 + qIdx * 0.05) : 500;
    const qty = rfq.items[0] ? rfq.items[0].quantity : 10;
    const totalAmount = Math.round(unitPrice * qty * 100) / 100;
    const taxAmount = Math.round(totalAmount * 0.18);

    return {
      id: `quo-${quoNum}`,
      quotationNumber: `QUO-${quoNum}`,
      rfqId: rfq.id,
      rfqNumber: rfq.rfqNumber,
      vendorId: vendor.id,
      vendorName: vendor.name,
      items: rfq.items.map((item, idx) => ({
        id: `quo-item-${quoNum}-${idx + 1}`,
        materialCode: item.materialCode,
        materialName: item.materialName,
        quantity: item.quantity,
        unitOfMeasure: item.unitOfMeasure,
        unitPrice: Math.round(unitPrice * 100) / 100,
        totalPrice: totalAmount,
        leadTimeDays: 14 + qIdx * 5,
        warrantyMonths: 36,
      })),
      subtotal: totalAmount,
      taxAmount,
      discountAmount: 0,
      totalAmount: totalAmount + taxAmount,
      currency: 'USD',
      deliveryTerms: 'DDP',
      paymentTerms: 'Net 30',
      validUntil: '2026-10-31',
      aiScore: 85 + qIdx * 4,
      isSelected: qIdx === 0 && rfq.status === 'Published',
      submittedAt: rfq.createdAt,
    };
  });
});

// --- 500+ Purchase Orders ---
const PO_STATUSES: PurchaseOrder['status'][] = [
  'Draft', 'PendingApproval', 'Approved', 'SentToVendor', 'PartiallyDelivered', 'FullyDelivered', 'Closed'
];

export const MOCK_PURCHASE_ORDERS: PurchaseOrder[] = MOCK_RFQS.map((rfq, i) => {
  const poNum = 5001 + i;
  const vendor = MOCK_VENDORS[i % MOCK_VENDORS.length]!;
  const req = MOCK_PURCHASE_REQUESTS[i % MOCK_PURCHASE_REQUESTS.length]!;
  const status = PO_STATUSES[i % PO_STATUSES.length]!;
  const totalAmount = req.totalAmount;

  return {
    id: `po-${poNum}`,
    poNumber: `PO-${poNum}`,
    sapPoNumber: status !== 'Draft' ? `450090${poNum}` : undefined,
    purchaseRequestId: req.id,
    requestNumber: req.requestNumber,
    rfqId: rfq.id,
    quotationId: `quo-${3001 + (i % 50)}`,
    vendorId: vendor.id,
    vendorName: vendor.name,
    items: req.items.map((item, idx) => ({
      id: `po-item-${poNum}-${idx + 1}`,
      poId: `po-${poNum}`,
      itemNumber: (idx + 1) * 10,
      materialCode: item.materialCode,
      materialName: item.materialName,
      quantity: item.quantity,
      unitOfMeasure: item.unitOfMeasure,
      unitPrice: item.estimatedUnitPrice,
      totalPrice: item.totalEstimatedPrice,
      costCenterCode: req.costCenterCode,
      plantCode: req.plantCode,
    })),
    totalAmount,
    currency: 'USD',
    status,
    paymentTerms: 'Net 30',
    deliveryDate: '2026-10-30',
    createdAt: req.createdAt,
    updatedAt: req.updatedAt,
  };
});

// --- 500+ Goods Receipts (GRNs) ---
export const MOCK_GOODS_RECEIPTS: GoodsReceiptNote[] = MOCK_PURCHASE_ORDERS.map((po, i) => {
  const grnNum = 6001 + i;
  return {
    id: `grn-${grnNum}`,
    grnNumber: `GRN-${grnNum}`,
    poId: po.id,
    poNumber: po.poNumber,
    vendorId: po.vendorId,
    vendorName: po.vendorName,
    plantCode: 'PLANT-1000',
    items: po.items.map((item) => ({
      id: `grn-item-${grnNum}-${item.itemNumber}`,
      materialCode: item.materialCode,
      materialName: item.materialName,
      orderedQuantity: item.quantity,
      receivedQuantity: item.quantity,
      acceptedQuantity: item.quantity,
      rejectedQuantity: 0,
      unitOfMeasure: item.unitOfMeasure,
    })),
    receivedDate: po.createdAt,
    inspectorName: 'Quality Lead John Miller',
    status: 'Accepted',
    sapGrnNumber: `500090${grnNum}`,
    createdAt: po.createdAt,
  };
});

// --- 500+ Invoices ---
export const MOCK_INVOICES: Invoice[] = Array.from({ length: 510 }, (_, i) => {
  const po = MOCK_PURCHASE_ORDERS[i % MOCK_PURCHASE_ORDERS.length]!;
  const grn = MOCK_GOODS_RECEIPTS[i % MOCK_GOODS_RECEIPTS.length]!;
  const invNum = 7001 + i;
  const invAmount = po.totalAmount;
  const taxAmount = Math.round(invAmount * 0.18);

  return {
    id: `inv-${invNum}`,
    invoiceNumber: `INV-${invNum}`,
    vendorInvoiceNumber: `VND-INV-${10000 + i}`,
    poId: po.id,
    poNumber: po.poNumber,
    grnId: grn.id,
    grnNumber: grn.grnNumber,
    vendorId: po.vendorId,
    vendorName: po.vendorName,
    invoiceAmount: invAmount,
    taxAmount,
    totalAmount: invAmount + taxAmount,
    currency: po.currency,
    threeWayMatchResult: {
      poMatch: true,
      grnMatch: true,
      priceVariancePercent: 0,
      qtyVariancePercent: 0,
      passed: true,
    },
    dueDate: '2026-11-30',
    status: i % 10 === 0 ? 'Matched3Way' : 'ApprovedForPayment',
    sapMiroNumber: `510090${invNum}`,
    createdAt: po.createdAt,
  };
});

// --- 500+ Payments ---
export const MOCK_PAYMENTS: Payment[] = Array.from({ length: 510 }, (_, i) => {
  const inv = MOCK_INVOICES[i % MOCK_INVOICES.length]!;
  const payNum = 8001 + i;

  return {
    id: `pay-${payNum}`,
    paymentNumber: `PAY-${payNum}`,
    invoiceId: inv.id,
    invoiceNumber: inv.invoiceNumber,
    vendorId: inv.vendorId,
    vendorName: inv.vendorName,
    amount: inv.totalAmount,
    currency: inv.currency,
    paymentMethod: i % 2 === 0 ? 'NEFT' : 'WireTransfer',
    paymentDate: inv.createdAt,
    bankReferenceNumber: `REF-ACH-${payNum}`,
    status: 'Completed',
    sapDocNumber: `1500${payNum}`,
    createdAt: inv.createdAt,
  };
});

// --- 500+ Notifications ---
export const MOCK_NOTIFICATIONS: Notification[] = Array.from({ length: 520 }, (_, i) => {
  const req = MOCK_PURCHASE_REQUESTS[i % MOCK_PURCHASE_REQUESTS.length]!;
  return {
    id: `notif-${i + 1}`,
    userId: req.requesterId,
    title: `Workflow Action Required: ${req.requestNumber}`,
    message: `Purchase request ${req.requestNumber} for ${req.title} requires your review.`,
    type: i % 3 === 0 ? 'ApprovalRequired' : i % 2 === 0 ? 'StatusChange' : 'System',
    isRead: i % 4 !== 0,
    createdAt: req.createdAt,
  };
});

// --- 1,000+ Audit Log Events ---
export const MOCK_AUDIT_LOGS: AuditLog[] = Array.from({ length: 1050 }, (_, i) => {
  const req = MOCK_PURCHASE_REQUESTS[i % MOCK_PURCHASE_REQUESTS.length]!;
  const user = MOCK_USERS[i % MOCK_USERS.length]!;
  const actions = ['REQUEST_CREATED', 'APPROVAL_SUBMITTED', 'NFA_GENERATED', 'PR_SYNCED', 'RFQ_ISSUED', 'PO_RELEASED', 'GRN_POSTED', 'INVOICE_MATCHED', 'PAYMENT_EXECUTED'];
  const action = actions[i % actions.length]!;

  return {
    id: `audit-${i + 1}`,
    userId: user.id,
    userName: user.name,
    userRole: user.role,
    action,
    entityType: action.split('_')[0]!,
    entityId: req.id,
    ipAddress: `192.168.1.${(i % 250) + 1}`,
    details: `Executed ${action} operation for entity reference ${req.requestNumber}`,
    timestamp: req.createdAt,
  };
});

// --- 500+ Integration Logs ---
export const MOCK_INTEGRATION_LOGS: IntegrationLog[] = Array.from({ length: 510 }, (_, i) => {
  const pr = MOCK_PRS[i % MOCK_PRS.length]!;
  return {
    id: `int-log-${i + 1}`,
    transactionId: `CORR-${String(10000 + i)}`,
    sourceSystem: 'ProcureAI',
    targetSystem: 'SAP',
    operationName: 'BAPI_PURCHASE_REQUISITION_CREATE',
    payloadSummary: JSON.stringify({ prNumber: pr.prNumber, amount: pr.totalValue }),
    responseStatus: i % 12 === 0 ? 'ERROR' : 'SUCCESS',
    responseCode: i % 12 === 0 ? '500' : '200',
    durationMs: 240,
    timestamp: pr.createdAt,
  };
});

export const MOCK_AI_RECOMMENDATIONS = [
  {
    id: 'ai-rec-1',
    recommendationType: 'OPTIMIZATION',
    confidenceScore: 0.94,
    title: 'Consolidated Sourcing Opportunity',
    summary: 'Consolidate 12 pending PRs for IT Hardware across Austin & Chicago plants to negotiate a 7.5% volume discount with Nexus Solutions Corp.',
    reasoning: [
      'Historical volume thresholds trigger 7.5% price tier at 50+ units.',
      'Cross-plant shipping alignment saves $4,200 in freight.',
      'Nexus Solutions Corp has 99.2% SLA compliance score.',
    ],
    suggestedAction: 'Create Combined RFQ Package',
  },
];

// ============================================================================
// DATA INTEGRITY VALIDATION UTILITY
// ============================================================================

export function validateMockDatabase(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  const userIds = new Set(MOCK_USERS.map((u) => u.id));
  const vendorIds = new Set(MOCK_VENDORS.map((v) => v.id));
  const requestIds = new Set(MOCK_PURCHASE_REQUESTS.map((r) => r.id));
  const poIds = new Set(MOCK_PURCHASE_ORDERS.map((p) => p.id));

  // Check Purchase Requests link to existing Users
  MOCK_PURCHASE_REQUESTS.forEach((req) => {
    if (!userIds.has(req.requesterId)) {
      errors.push(`Orphan Request found: ${req.id} links to missing user ${req.requesterId}`);
    }
  });

  // Check NFAs link to existing Requests
  MOCK_NFA.forEach((nfa) => {
    if (!requestIds.has(nfa.purchaseRequestId)) {
      errors.push(`Orphan NFA found: ${nfa.id} links to missing request ${nfa.purchaseRequestId}`);
    }
  });

  // Check POs link to existing Vendors & Requests
  MOCK_PURCHASE_ORDERS.forEach((po) => {
    if (!vendorIds.has(po.vendorId)) {
      errors.push(`Orphan PO found: ${po.id} links to missing vendor ${po.vendorId}`);
    }
  });

  // Check GRNs link to existing POs
  MOCK_GOODS_RECEIPTS.forEach((grn) => {
    if (!poIds.has(grn.poId)) {
      errors.push(`Orphan GRN found: ${grn.id} links to missing PO ${grn.poId}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}
